import { ChevronDown, RefreshCw } from "lucide-react";
import HelpQ from "./HelpQ";
import { writeDraggedChatContext } from "../utils/chatContextDrag";

// Assessments that can be (re)generated directly from the rail when missing.
// Each entry can require an integration before the Generate button is shown.
const REFRESHABLE_ASSESSMENT_IDS = new Set(["conversion"]);
// Assessments whose generation depends on a Google (GA4) connection.
const GOOGLE_DEPENDENT_ASSESSMENT_IDS = new Set(["conversion"]);

function AppRightRailAssessment({
  open,
  assessments,
  highlightedAssessmentIds,
  activeSummaryId,
  chatContextPickMode,
  onToggle,
  onOpenAssessment,
  onRefreshAssessment,
  refreshingAssessmentId,
  hasGoogleConnection = false,
  styles,
}) {
  return (
    <section className={styles.rightRailSection} data-tour-id="assessments">
      <button
        type="button"
        className={`${styles.rightRailSectionHeader} ${
          open ? styles.rightRailSectionHeaderOpen : ""
        }`}
        onClick={onToggle}
      >
        <span className={styles.rightRailSectionHeaderTitle}>Assessments</span>
        <span className={styles.rightRailSectionHeaderActions}>
          <HelpQ stepIndex={3} title="What are assessments?" />
          <ChevronDown size={12} strokeWidth={2} aria-hidden="true" />
        </span>
      </button>
      {open ? (
        <div className={styles.rightRailSectionBody}>
          {assessments.map((item) => {
            const requiresGoogle = GOOGLE_DEPENDENT_ASSESSMENT_IDS.has(item.id);
            const canRefresh =
              REFRESHABLE_ASSESSMENT_IDS.has(item.id) &&
              !item.latestUpdatedAt &&
              typeof onRefreshAssessment === "function" &&
              (!requiresGoogle || hasGoogleConnection);

            // When the report is missing but regeneratable, show a Generate
            // button in place of the (non-clickable) "Not generated yet" row.
            if (canRefresh) {
              const isRefreshing = refreshingAssessmentId === item.id;
              return (
                <div
                  key={item.id}
                  className={styles.rightRailAssessmentItem}
                  style={{ cursor: "default" }}
                  data-tour-id={
                    item.id === "business" ? "biz-assessment" : undefined
                  }
                >
                  <span className={styles.rightRailAssessmentLeft}>
                    <span className={styles.rightRailAssessmentName}>
                      {item.label}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={styles.rightRailAssessmentRefreshBtn}
                    onClick={() => onRefreshAssessment(item)}
                    disabled={isRefreshing}
                    title="Generate this report"
                  >
                    <RefreshCw
                      size={11}
                      strokeWidth={2}
                      aria-hidden="true"
                      className={isRefreshing ? styles.rightRailRefreshSpin : ""}
                    />
                    <span>{isRefreshing ? "Generating…" : "Generate"}</span>
                  </button>
                </div>
              );
            }

            return (
            <button
              key={item.id}
              type="button"
              className={`${styles.rightRailAssessmentItem} ${
                highlightedAssessmentIds.has(item.id)
                  ? styles.rightRailAssessmentItemActive
                  : ""
              } ${
                activeSummaryId === item.id
                  ? styles.rightRailAssessmentItemSelected
                  : ""
              } ${
                !item.latestUpdatedAt ? styles.rightRailItemDisabled : ""
              } ${
                chatContextPickMode && item.latestUpdatedAt
                  ? styles.rightRailItemWiggle
                  : ""
              }`}
              onClick={() => {
                if (!item.latestUpdatedAt) return;
                onOpenAssessment(item);
              }}
              disabled={!item.latestUpdatedAt}
              draggable={Boolean(chatContextPickMode && item.latestUpdatedAt)}
              onDragStart={(event) =>
                writeDraggedChatContext(event, {
                  key: item.id,
                  label: item.label,
                  type: "assessment",
                  source: "assessment",
                  sourceLabel: "Assessment",
                })
              }
              data-tour-id={item.id === "business" ? "biz-assessment" : undefined}
            >
              <span className={styles.rightRailAssessmentLeft}>
                <span className={styles.rightRailAssessmentName}>{item.label}</span>
              </span>
              <span className={styles.rightRailAssessmentMeta}>
                {item.meta || "Not generated yet"}
              </span>
            </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

export default AppRightRailAssessment;
